import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import TextContainer from "components/htmlElements/Text";
import SubTitle from "components/SubTitle";
import { Col, Form, Row } from "react-bootstrap";
import { Controller } from "react-hook-form";
import TypeAheadReuseable from "components/bootstrap/TypeAheadReuseable";
import {
  decisionTypeAheadProps,
  violationTypeAheadProps,
} from "components/Reports/reportAssets/typeAheadProps";
import RenderPayAttentionToPolicyAnnouncment from "components/Reports/reportAssets/RenderPayAttentionToPolicyAnnouncment";
import { useRole } from "hooks/useRole";
import {
  ACCORDING_TO_CIRCUMSTANCES,
  DecisionTypesCode,
  UserRole,
} from "components/Reports/reportAssets/assets";
import { DBViolation } from "models/ReportViolationType";
import {
  useGetDecisionTypesListQuery,
  useGetViolationsListQuery,
} from "features/api/contentManagement/contentManagementApiSlice";
import { tDecisionType } from "i18n/enums";
import { FormStepsProps } from "../FormStepsProps";
import DetailsAndCommentsTextArea from "./DetailsAndCommentsTextArea";
import RendercomplementaryDropDown from "./RendercomplementaryDropDowns/RendercomplementaryDropDown";
import RenderNoteToMainController from "./RenderNoteToMainController";

const DecisionFormSection = ({
  reportIsDuplicate,
  control,
  errors,
  setValue,
  getValues,
}: FormStepsProps) => {
  const { t } = useTranslation();
  const { data: violations } = useGetViolationsListQuery();
  const { data: decisionTypes } = useGetDecisionTypesListQuery();
  const offenceTypeIDCurrent = getValues("offenceTypeID");
  const decisionTypeIdCurrent = getValues("decisionTypeID");

  const role = useRole();

  const offencesOptions = violations || [];

  const decisionOptions = (decisionTypes || [])
    ?.filter((item) => {
      if (!reportIsDuplicate) return item.id !== DecisionTypesCode.Testing;
      return item;
    })
    .map((item) => {
      return {
        // D-08: server provides the stable code (item.id); the DISPLAY label is
        // localized client-side via the i18n lookup (not the raw Hebrew DB name).
        label: tDecisionType(item.id),
        value: item.id,
        disabled: item.id === DecisionTypesCode.Obsolescence,
      };
    });

  const policyText = useMemo(() => {
    const selectedViolationObj: DBViolation | undefined = violations?.find(
      (offenceObj: DBViolation) => offenceObj.id === offenceTypeIDCurrent
    );

    return selectedViolationObj?.policy ?? null;
  }, [violations, offenceTypeIDCurrent]);

  const handelSelectOffenceValue = (selectedValue) => {
    const selectedViolationObj: DBViolation = violations?.find(
      (offenceObj: DBViolation) => offenceObj.id === selectedValue?.[0]?.value
    );

    if (selectedViolationObj) {
      const isAccordingTocircumstances =
        selectedViolationObj?.policy?.trim() === ACCORDING_TO_CIRCUMSTANCES;

      setValue(
        "decisionTypeID",
        !isAccordingTocircumstances ? selectedViolationObj?.decisionTypeID : []
      );
    } else {
      setValue("decisionTypeID", []);
    }
  };

  const isOppositeDecision = useMemo(() => {
    const selectedViolationObj: DBViolation | undefined = violations?.find(
      (offenceObj: DBViolation) =>
        offenceObj.id === Number(offenceTypeIDCurrent)
    );
    const isAccordingToCircumstances =
      selectedViolationObj?.policy?.trim() === ACCORDING_TO_CIRCUMSTANCES;

    return (
      selectedViolationObj?.decisionTypeID !== decisionTypeIdCurrent &&
      !isAccordingToCircumstances
    );
  }, [violations, offenceTypeIDCurrent, decisionTypeIdCurrent]);

  const getComment = useCallback(() => {
    if (!isOppositeDecision) {
      return null;
    }

    if (role === UserRole.REGULAR) {
      return t("payAttentionToPolicy");
    }

    return null;
  }, [isOppositeDecision, role, t]);

  const commentToSeniorController = getComment();

  const handelSelected = (value, optionsName) => {
    if (!value) return [];
    if (typeof value !== "number" && !value?.length) return value;
    let optionsArray;
    if (optionsName === "offenceTypeID") {
      optionsArray = offencesOptions?.map((item) => {
        return {
          label: item.name,
          value: item.id,
        };
      });
    }
    if (optionsName === "decisionTypeID") {
      optionsArray = decisionOptions;
    }
    const selected = optionsArray.find(
      (option) => option.value === Number(value)
    );

    return [selected];
  };

  return (
    <section className="DecisionFormSection-container default-lower-padding">
      <SubTitle underLine className="extra-lower-padding">
        {t("labelDescision")}
      </SubTitle>
      <Form.Label className="full-width-label">
        {t("offenceTypeName")}
        <Row className="violation-type-container default-vertical-padding">
          <Col sm={6}>
            {offencesOptions?.length ? (
              <Controller
                name="offenceTypeID"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("validationRequired"),
                  },
                }}
                render={({ field: { onChange, value, name } }) => (
                  <>
                    <TypeAheadReuseable
                      {...violationTypeAheadProps}
                      options={
                        offencesOptions
                          // Operator handling: show ALL enabled offence types from the
                          // OffenseType table (only disabled ones are hidden). The prior
                          // `!forVolunteer` filter stripped every row when the table holds
                          // volunteer-flagged offence types, leaving the selector empty.
                          ?.filter((v) => !v.disable)
                          ?.map((item) => {
                            return {
                              label: item.name,
                              value: item.id,
                            };
                          }) || []
                      }
                      onChange={(e) => {
                        onChange(e);
                        setValue("offenceTypeID", e[0]?.value || "");
                        handelSelectOffenceValue(e);
                      }}
                      selected={handelSelected(value, "offenceTypeID")}
                    />
                    {errors.offenceTypeID && (
                      <span className="requiredField">
                        *{t("validationRequired")}
                      </span>
                    )}
                  </>
                )}
              />
            ) : (
              <div>Loading...</div>
            )}
          </Col>
          {policyText && (
            <Col sm={8}>
              <TextContainer type="span" bold>
                {t("policy")}:<span>&nbsp;</span>
                {policyText}
              </TextContainer>
            </Col>
          )}
        </Row>
      </Form.Label>

      <Form.Label className="full-width-label">
        {t("labelDescision")}

        <Row className="decision-container extra-lower-padding">
          <Col sm={6}>
            {decisionOptions?.length ? (
              <Controller
                name="decisionTypeID"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("validationRequired"),
                  },
                }}
                render={({ field: { onChange, value, name } }) => (
                  <>
                    <TypeAheadReuseable
                      {...decisionTypeAheadProps}
                      options={decisionOptions}
                      onChange={(e) => {
                        onChange(e);
                        setValue("decisionTypeID", e[0]?.value || "");
                        // handelSelectValue(e, name);
                      }}
                      selected={handelSelected(value, "decisionTypeID")}
                    />
                    {errors.decisionTypeID && (
                      <span className="requiredField">
                        *{t("validationRequired")}
                      </span>
                    )}
                  </>
                )}
              />
            ) : (
              <div>Loading...</div>
            )}
          </Col>
          {isOppositeDecision && (
            <RenderPayAttentionToPolicyAnnouncment
              suggestedPolicy={policyText}
            />
          )}
        </Row>
      </Form.Label>

      <RendercomplementaryDropDown
        control={control}
        errors={errors}
        setValue={setValue}
        getValues={getValues}
      />

      <RenderNoteToMainController
        placeholder={commentToSeniorController}
        control={control}
        errors={errors}
      />
      <DetailsAndCommentsTextArea control={control} errors={errors} />
    </section>
  );
};
export default DecisionFormSection;
